import { Link, Navigate } from "react-router-dom";
import { ArrowRight, Check, CheckCircle2, Users } from "lucide-react";
import { Container, PageFade, Reveal } from "../components/site/Motion";
import { ComponentList } from "../components/site/Forfait";
import { BookingWidget } from "../components/site/BookingWidget";
import { store } from "../lib/api";
import { useForfaits } from "../lib/useForfaits";

const Addon = ({ addon }) => (
  <div className="rounded-2xl border border-[#650D23]/20 bg-[#650D23]/[0.04] p-6" data-testid="result-addon">
    <h3 className="flex items-center gap-2 text-xl text-[#650D23]"><Users className="h-5 w-5" />{addon.title}</h3>
    <p className="mt-2 text-sm text-[#242020]/75">{addon.description}</p>
    <ul className="mt-4 grid gap-2 sm:grid-cols-2">
      {addon.items.map((t) => <li key={t} className="flex items-start gap-2 text-sm"><CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-[#650D23]" />{t}</li>)}
    </ul>
  </div>
);

const ForfaitCard = ({ f, reco }) => (
  <div className="grid overflow-hidden rounded-[2rem] border border-[#EDE3D9] bg-[#FFFDFB] shadow-[0_40px_80px_-50px_rgba(74,12,27,.55)] lg:grid-cols-[1.35fr_1fr]" data-testid="result-main-forfait">
    <div className="space-y-8 p-7 sm:p-12">
      <div>
        <p className="eyebrow">Votre forfait personnalisé STRUKTIV</p>
        <h2 className="mt-4 text-4xl uppercase leading-tight tracking-wide text-[#650D23] sm:text-5xl" data-testid="result-forfait-name">
          {f.name}{f.addon && <span className="block text-2xl sm:text-3xl">+ {f.addon.title}</span>}
        </h2>
        <p className="mt-4 text-lg text-[#242020]/80" data-testid="result-forfait-tagline">{f.tagline}</p>
        <p className="mt-3 leading-relaxed text-[#242020]/75" data-testid="result-explanation">{reco.explanation}</p>
      </div>
      <div className="grid gap-8 md:grid-cols-2">
        <div><h3 className="mb-4 text-xl">Protections et stratégies comprises</h3><ComponentList items={f.components} testid="result-forfait-components" /></div>
        <div><h3 className="mb-4 text-xl">Principaux avantages</h3>
          <ul className="space-y-3" data-testid="result-forfait-advantages">
            {f.advantages.map((a) => <li key={a} className="flex items-start gap-2.5 text-[.95rem]"><CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-[#650D23]" />{a}</li>)}
          </ul></div>
      </div>
      {f.addon && <Addon addon={f.addon} />}
      <div className="flex flex-wrap items-center gap-4">
        <Link to={`/forfaits/${f.slug}`} className="btn-outline" data-testid="result-forfait-detail-link">En savoir plus <ArrowRight className="h-4 w-4" /></Link>
        <a href="#reservation" className="btn-primary" data-testid="result-booking-anchor">Planifier ma consultation <ArrowRight className="h-4 w-4" /></a>
      </div>
      <p className="text-xs text-[#242020]/60" data-testid="result-disclaimer">Ce forfait constitue une orientation préliminaire, établie à partir de vos réponses. Sa pertinence sera validée lors d'une consultation. Il ne constitue pas une recommandation financière personnalisée.</p>
    </div>
    <img src="/img/beaute4.jpg" alt="" className="hidden h-full w-full object-cover lg:block" />
  </div>
);

export default function Resultat() {
  const result = store.get("struktiv_result");
  const forfaits = useForfaits();
  if (!result) return <Navigate to="/secteurs" replace />;
  const reco = result.recommendation;
  const main = forfaits?.find((f) => f.slug === reco.forfait_slug);

  return (
    <PageFade>
      <section className="wave-bg">
        <Container className="py-14 lg:py-20">
          <Reveal className="mx-auto max-w-2xl text-center">
            <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-[#650D23] text-[#FFFDFB]"><Check className="h-6 w-6" /></span>
            <h1 className="mt-6 text-4xl sm:text-5xl lg:text-6xl" data-testid="result-title">Merci pour vos réponses!</h1>
            <p className="mt-5 text-lg text-[#242020]/80">« Selon les informations que vous nous avez fournies, voici la solution STRUKTIV qui pourrait correspondre à votre réalité professionnelle. »</p>
            <p className="mt-3 text-sm text-[#242020]/60" data-testid="result-profile">{result.secteur_label} · {result.metier_label}</p>
          </Reveal>
          <Reveal delay={0.1} className="mt-12">
            {!forfaits ? <div className="h-96 animate-pulse rounded-[2rem] bg-[#EDE3D9]/60" /> : main && <ForfaitCard f={main} reco={reco} />}
          </Reveal>
        </Container>
      </section>
      <section id="reservation" className="relative overflow-hidden bg-[#650D23] text-[#FFFDFB]" data-testid="result-booking-section">
        <img src="/img/pont1.jpg" alt="" aria-hidden="true" className="absolute inset-0 h-full w-full object-cover opacity-30 mix-blend-luminosity" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#4A0C1B]/85 to-[#650D23]/80" aria-hidden="true" />
        <Container className="relative py-16 lg:py-24">
          <Reveal className="max-w-2xl">
            <p className="eyebrow !text-[#EDE3D9]">Consultation gratuite</p>
            <h2 className="mt-4 text-4xl sm:text-5xl">Planifions votre consultation gratuite!</h2>
            <p className="mt-4 text-lg text-[#EDE3D9]">« Prenons quelques minutes pour discuter de votre situation et explorer ensemble les solutions qui pourraient vous convenir. »</p>
          </Reveal>
          <Reveal delay={0.1} className="mt-10"><BookingWidget leadId={result.lead_id} /></Reveal>
        </Container>
      </section>
    </PageFade>
  );
}
